import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { MathJaxContext, MathJax } from "better-react-mathjax";

const MyTextEditor = ({
  initialContent = "",
  onChange,
  onImageUpload,
  imageLibrary = [],
  upload_file,
  config,
  setuploadEffect,
  refresh_img,
  setShowUploadModal
}) => {
 const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [isImageLibraryModalOpen, setIsImageLibraryModalOpen] = useState(false);
  const [isFormulaModalOpen, setIsFormulaModalOpen] = useState(false);
  const [isTableModalOpen, setIsTableModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Form state for modals only
  const [linkUrl, setLinkUrl] = useState("");
  const [linkText, setLinkText] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [formulaText, setFormulaText] = useState("");
  const [formulaType, setFormulaType] = useState("inline"); // "inline" or "block"
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);

  // fonts
  const [fontSize, setFontSize] = useState("16px");
  const [fontFamily, setFontFamily] = useState("Arial");

  // Font options
  const fontOptions = [
    { value: "Arial, sans-serif", label: "Arial" },
    { value: "'Times New Roman', serif", label: "Times New Roman" },
    { value: "Georgia, serif", label: "Georgia" },
    { value: "'Courier New', monospace", label: "Courier New" },
    { value: "Verdana, sans-serif", label: "Verdana" },
    { value: "'Trebuchet MS', sans-serif", label: "Trebuchet MS" }
  ];

  // Font sizes
  const fontSizeOptions = [
    { value: "12px", label: "Small" },
    { value: "16px", label: "Normal" },
    { value: "18px", label: "Medium" },
    { value: "24px", label: "Large" },
    { value: "32px", label: "X-Large" }
  ];

  const editorRef = useRef(null);
  const selectionRef = useRef(null);
  const isComposingRef = useRef(false);
  const isUpdatingContentRef = useRef(false);

  // MathJax configuration
  const mathJaxConfig = {
    loader: { load: ["[tex]/html"] },
    tex: {
      packages: { "[+]": ["html"] },
      inlineMath: [
        ["$", "$"],
        ["\\(", "\\)"]
      ],
      displayMath: [
        ["$$", "$$"],
        ["\\[", "\\]"]
      ]
    }
  };

  // Improved sanitizeHTML function
  const sanitizeHTML = (html) => {
    if (!html || html.trim() === '') return '';
    
    // Only remove RTL-specific spans and styles, preserve other HTML
    return html
      .replace(/<span[^>]*dir=["']rtl["'][^>]*>/gi, '')
      .replace(/<span[^>]*style=["'][^"']*direction:\s*rtl[^"']*["'][^>]*>/gi, '')
      .replace(/<span[^>]*style=["'][^"']*text-align:\s*right[^"']*["'][^>]*>/gi, '')
      .replace(/<span[^>]*dir=["']rtl["'][^>]*><\/span>/gi, '')
      .replace(/style="[^"]*direction:\s*rtl[^"]*"/gi, '')
      .replace(/style="[^"]*text-align:\s*right[^"]*"/gi, '')
      .trim() || '';
  };

  // Initialize editor content - FIXED: watches initialContent changes
  useEffect(() => {
    if (editorRef.current) {
      const contentToSet = initialContent && initialContent.trim() !== '' 
        ? sanitizeHTML(initialContent) 
        : '<p></p>';
      
      // Only update if content actually changed
      if (editorRef.current.innerHTML !== contentToSet) {
        editorRef.current.innerHTML = contentToSet;
        
        // Trigger MathJax rendering after content update
        setTimeout(() => {
          if (window.MathJax) {
            window.MathJax.typesetPromise?.();
          }
        }, 100);
      }
    }
  }, [initialContent]); // Now watches for initialContent changes

  // Auto-render MathJax when content changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (window.MathJax) {
        window.MathJax.typesetPromise?.();
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [editorRef.current?.innerHTML]);

  // Save current selection properly
  const saveSelection = () => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    
    try {
      const range = sel.getRangeAt(0);
      const editor = editorRef.current;
      
      if (editor.contains(range.commonAncestorContainer)) {
        selectionRef.current = {
          startContainer: range.startContainer,
          startOffset: range.startOffset,
          endContainer: range.endContainer,
          endOffset: range.endOffset
        };
      }
    } catch (err) {
      console.warn("saveSelection error:", err);
    }
  };

  // Restore saved selection properly
  const restoreSelection = () => {
    const savedSelection = selectionRef.current;
    if (!savedSelection || !editorRef.current) return;
    
    try {
      const sel = window.getSelection();
      const range = document.createRange();
      
      range.setStart(savedSelection.startContainer, savedSelection.startOffset);
      range.setEnd(savedSelection.endContainer, savedSelection.endOffset);
      
      sel.removeAllRanges();
      sel.addRange(range);
    } catch (err) {
      // If restoration fails, set cursor to end of editor
      const editor = editorRef.current;
      const range = document.createRange();
      const sel = window.getSelection();
      
      range.selectNodeContents(editor);
      range.collapse(false);
      
      sel.removeAllRanges();
      sel.addRange(range);
    }
  };

  // Event handlers - simplified to only depend on DOM
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const handleInput = () => {
      if (isComposingRef.current) return;

      const newContent = editor.innerHTML;
      isUpdatingContentRef.current = true;
      onChange?.(newContent);
    };

    const handleCompositionStart = () => {
      isComposingRef.current = true;
    };

    const handleCompositionEnd = () => {
      isComposingRef.current = false;
      handleInput();
    };

    const handlePaste = (e) => {
      e.preventDefault();
      saveSelection();

      const text = (e.clipboardData || window.clipboardData).getData("text/plain");
      document.execCommand("insertText", false, text);

      setTimeout(() => {
        restoreSelection();
        handleInput();
      }, 0);
    };

    const handleKeyDown = (e) => {
      saveSelection();
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case 'b':
            e.preventDefault();
            applyCommand("bold");
            break;
          case 'i':
            e.preventDefault();
            applyCommand("italic");
            break;
          case 'u':
            e.preventDefault();
            applyCommand("underline");
            break;
          case 'k':
            e.preventDefault();
            setIsLinkModalOpen(true);
            break;
          default:
            return;
        }
      }
      
      const rtlChars = /[\u0590-\u05FF\u0600-\u06FF\u0750-\u077F]/;
      if (rtlChars.test(e.key)) {
        e.preventDefault();
        toast.info("RTL characters are not supported in this editor.");
      }
    };

    const handleMouseUp = () => {
      saveSelection();
    };
    
    const handleKeyUp = () => {
      saveSelection();
    };

    editor.addEventListener("input", handleInput);
    editor.addEventListener("compositionstart", handleCompositionStart);
    editor.addEventListener("compositionend", handleCompositionEnd);
    editor.addEventListener("paste", handlePaste);
    editor.addEventListener("keydown", handleKeyDown);
    editor.addEventListener("mouseup", handleMouseUp);
    editor.addEventListener("keyup", handleKeyUp);

    return () => {
      editor.removeEventListener("input", handleInput);
      editor.removeEventListener("compositionstart", handleCompositionStart);
      editor.removeEventListener("compositionend", handleCompositionEnd);
      editor.removeEventListener("paste", handlePaste);
      editor.removeEventListener("keydown", handleKeyDown);
      editor.removeEventListener("mouseup", handleMouseUp);
      editor.removeEventListener("keyup", handleKeyUp);
    };
  }, [onChange]);

  // Apply command directly to DOM without React state updates
  const applyCommand = (command, value = null) => {
    saveSelection();
    
    try {
      if (command === "insertHTML" && value) {
        document.execCommand("insertHTML", false, value);
      } else {
        document.execCommand(command, false, value);
      }
    } catch (error) {
      console.warn(`Command ${command} failed:`, error);
    }
    
    setTimeout(() => {
      editorRef.current.focus();
      restoreSelection();
      
      // Only update parent component, not local state
      const newContent = editorRef.current.innerHTML;
      onChange?.(newContent);
      
      // Re-render MathJax after any content change
      if (window.MathJax) {
        window.MathJax.typesetPromise?.();
      }
    }, 10);
  };

  // Simplified command handlers
  const handleButtonClick = (command, value = null) => {
    applyCommand(command, value);
  };

  const handleHeadingChange = (level) => {
    applyCommand("formatBlock", `<h${level}>`);
  };

  const handleParagraphFormat = () => {
    applyCommand("formatBlock", "<p>");
  };

  const handleAlignment = (alignment) => {
    applyCommand(alignment);
  };

  const applyStyle = (styleName, value) => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);

    // Extract the selected contents
    const selectedContent = range.extractContents();

    // Create a span wrapper
    const span = document.createElement("span");
    span.style[styleName] = value;

    // Put the selected content inside the span
    span.appendChild(selectedContent);

    // Insert back into the DOM
    range.insertNode(span);

    // Move cursor after the styled content
    range.setStartAfter(span);
    range.setEndAfter(span);
    selection.removeAllRanges();
    selection.addRange(range);
  };

  // Font Family
  const handleFontFamilyChange = (font) => {
    applyStyle("fontFamily", font);
    setFontFamily(font);
  };

  // Font Size
  const handleFontSizeChange = (size) => {
    const normalized = size.endsWith("px") ? size : `${size}px`;
    applyStyle("fontSize", normalized);
    setFontSize(normalized);
  };

  const clearFormatting = () => {
    applyCommand("removeFormat");
    applyCommand("unlink");
  };

  const handleLinkInsert = () => {
    if (linkUrl) {
      restoreSelection();
      let linkHTML;
      if (linkText) {
        linkHTML = `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer">${linkText}</a>`;
      } else {
        const selection = window.getSelection();
        const selectedText = selection.toString().trim();
        linkHTML = `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer">${selectedText || linkUrl}</a>`;
      }
      applyCommand("insertHTML", linkHTML);

      setLinkUrl("");
      setLinkText("");
      setIsLinkModalOpen(false);
    }
  };

  const handleImageInsert = () => {
    if (imageUrl) {
      restoreSelection();
      const imgHTML = `<img src="${imageUrl}" alt="Inserted image" style="max-width:100%; height:auto; display:block; margin:10px 0;" />`;
      applyCommand("insertHTML", imgHTML);

      setImageUrl("");
      setIsImageModalOpen(false);
    }
  };

  const handleImageFromLibrary = (imgUrl) => {
    const imgHTML = `<img src="${imgUrl}" alt="Library image" style="max-width: 100%; height: auto; display: block; margin: 10px 0;" />`;
    applyCommand("insertHTML", imgHTML);
    setIsImageLibraryModalOpen(false);
  };

  // Fixed formula insertion with MathJax rendering
  const handleFormulaInsert = () => {
    if (formulaText) {
      restoreSelection();

      let formulaHTML;
      if (formulaType === "block") {
        formulaHTML = `<div class="math-formula" style="text-align: center; width: 100%; margin: 10px 0;">\\[${formulaText}\\]</div>`;
      } else {
        formulaHTML = `<span class="math-formula" style="display: inline-block; margin: 0 2px;">\\(${formulaText}\\)</span>`;
      }

      applyCommand("insertHTML", formulaHTML);

      // Immediate re-render for MathJax
      setTimeout(() => {
        if (window.MathJax) {
          window.MathJax.typesetPromise?.();
        }
      }, 0);

      setFormulaText("");
      setIsFormulaModalOpen(false);
    }
  };

  const handleTableInsert = (e) => {
    if (e) e.preventDefault();
    
    if (tableRows > 0 && tableCols > 0) {
      let tableHTML = '<table border="1" style="border-collapse: collapse; width: 100%; margin: 10px 0;"><tbody>';
      
      for (let i = 0; i < tableRows; i++) {
        tableHTML += '<tr>';
        for (let j = 0; j < tableCols; j++) {
          tableHTML += `<td style="padding: 8px; border: 1px solid #ccc; min-width: 50px;">&nbsp;</td>`;
        }
        tableHTML += '</tr>';
      }
      
      tableHTML += '</tbody></table>';
      applyCommand("insertHTML", tableHTML);
      
      setTableRows(3);
      setTableCols(3);
      setIsTableModalOpen(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!onImageUpload) {
      // Local file handling
      const reader = new FileReader();
      reader.onload = (event) => {
        const imgHTML = `<img src="${event.target.result}" alt="Uploaded image" style="max-width: 100%; height: auto; display: block; margin: 10px 0;" />`;
        applyCommand("insertHTML", imgHTML);
      };
      reader.readAsDataURL(file);
      return;
    }

    setIsUploading(true);
    setuploadEffect?.(true);

    try {
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error("File size exceeds the 5MB limit. Please select a smaller file.");
        return;
      }

      const formData = new FormData();
      formData.append("uploaded_file", file);

      const response = await axios.post(upload_file, formData, config);

      if (response.data?.imageUrl) {
        const imgHTML = `<img src="${response.data.imageUrl}" alt="Uploaded image" style="max-width: 100%; height: auto; display: block; margin: 10px 0;" />`;
        applyCommand("insertHTML", imgHTML);
        toast.success("Image uploaded successfully!");
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      console.error("Image upload failed:", error);
      toast.error(error.response?.data?.message || "Upload failed. Please try again.");
    } finally {
      setIsUploading(false);
      setuploadEffect?.(false);
      setIsImageModalOpen(false);
      refresh_img?.();
      setShowUploadModal?.(false);
    }
  };

  // Debug function to check current content
  const debugContent = () => {
    console.log('Current editor content:', editorRef.current?.innerHTML);
    console.log('Initial content prop:', initialContent);
    console.log('Sanitized content:', sanitizeHTML(initialContent));
  };

  // Modal component
  const Modal = ({ isOpen, onClose, title, children, size = "md" }) => {
    if (!isOpen) return null;
    
    const sizeClasses = {
      sm: "w-11/12 md:w-1/3 lg:w-1/4",
      md: "w-11/12 md:w-3/4 lg:w-1/2",
      lg: "w-11/12 md:w-5/6 lg:w-3/4"
    };

    
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className={`bg-white rounded-lg shadow-xl max-h-screen overflow-y-auto ${sizeClasses[size]}`}>
          <div className="flex justify-between items-center p-4 border-b">
            <h3 className="text-lg font-semibold">{title}</h3>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
              type="button"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="p-4">{children}</div>
        </div>
      </div>
    );
  };

  return (
    <MathJaxContext version={3} config={mathJaxConfig}>
      <div className="editor-container w-full border border-gray-300 rounded-lg overflow-hidden shadow-sm">
        {/* Toolbar */}
        <div className="toolbar flex flex-wrap items-center p-2 bg-gray-50 border-b border-gray-300 gap-1">
          {/* Format Selector */}
          <div className="flex items-center mr-2">
            <select
              onChange={(e) => {
                const val = e.target.value;
                if (val === "p") {
                  handleParagraphFormat();
                } else {
                  const level = val.replace("h", "");
                  handleHeadingChange(level);
                }
              }}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              title="Format"
            >
              <option value="p">Paragraph</option>
              <option value="h1">Heading 1</option>
              <option value="h2">Heading 2</option>
              <option value="h3">Heading 3</option>
              <option value="h4">Heading 4</option>
              <option value="h5">Heading 5</option>
              <option value="h6">Heading 6</option>
            </select>
          </div>

          {/* Font Family */}
          <div className="flex items-center mr-2">
            <select
              value={fontFamily}
              onChange={(e) => handleFontFamilyChange(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              title="Font Family"
            >
              {fontOptions.map((font) => (
                <option key={font.value} value={font.value}>{font.label}</option>
              ))}
            </select>
          </div>

          {/* Font Size */}
          <div className="flex items-center mr-2">
            <select
              value={fontSize}
              onChange={(e) => handleFontSizeChange(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              title="Font Size"
            >
              {fontSizeOptions.map((size) => (
                <option key={size.value} value={size.value}>{size.label}</option>
              ))}
            </select>
          </div>

          {/* Text Formatting */}
          <div className="flex border border-gray-300 rounded-md overflow-hidden mr-2">
            <button
              onClick={() => handleButtonClick("bold")}
              className="px-3 py-1.5 bg-white hover:bg-gray-100 focus:outline-none focus:bg-gray-200 border-r border-gray-300"
              title="Bold (Ctrl+B)"
              type="button"
            >
              <span className="font-bold">B</span>
            </button>
            <button
              onClick={() => handleButtonClick("italic")}
              className="px-3 py-1.5 bg-white hover:bg-gray-100 focus:outline-none focus:bg-gray-200 border-r border-gray-300"
              title="Italic (Ctrl+I)"
              type="button"
            >
              <span className="italic">I</span>
            </button>
            <button
              onClick={() => handleButtonClick("underline")}
              className="px-3 py-1.5 bg-white hover:bg-gray-100 focus:outline-none focus:bg-gray-200"
              title="Underline (Ctrl+U)"
              type="button"
            >
              <span className="underline">U</span>
            </button>
          </div>

          {/* Text Alignment */}
          <div className="flex border border-gray-300 rounded-md overflow-hidden mr-2">
            <button
              onClick={() => handleAlignment("justifyLeft")}
              className="px-3 py-1.5 bg-white hover:bg-gray-100 focus:outline-none border-r border-gray-300"
              title="Align Left"
              type="button"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 16 16">
                <path fillRule="evenodd" d="M2 12.5a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5zm0-3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5zm0-3a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5zm0-3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5z"/>
              </svg>
            </button>
            <button
              onClick={() => handleAlignment("justifyCenter")}
              className="px-3 py-1.5 bg-white hover:bg-gray-100 focus:outline-none border-r border-gray-300"
              title="Align Center"
              type="button"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 16 16">
                <path fillRule="evenodd" d="M4 12.5a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5zm-2-3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5zm2-3a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5zm-2-3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5z"/>
              </svg>
            </button>
            <button
              onClick={() => handleAlignment("justifyRight")}
              className="px-3 py-1.5 bg-white hover:bg-gray-100 focus:outline-none border-r border-gray-300"
              title="Align Right"
              type="button"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 16 16">
                <path fillRule="evenodd" d="M6 12.5a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5zm-2-3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5zm2-3a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5zm-2-3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5z"/>
              </svg>
            </button>
            <button
              onClick={() => handleAlignment("justifyFull")}
              className="px-3 py-1.5 bg-white hover:bg-gray-100 focus:outline-none"
              title="Justify"
              type="button"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 16 16">
                <path fillRule="evenodd" d="M2 12.5a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5zm0-3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5zm0-3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5zm0-3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5z"/>
              </svg>
            </button>
          </div>

          {/* Lists */}
          <div className="flex border border-gray-300 rounded-md overflow-hidden mr-2">
            <button
              onClick={() => handleButtonClick("insertOrderedList")}
              className="px-3 py-1.5 bg-white hover:bg-gray-100 focus:outline-none focus:bg-gray-200 border-r border-gray-300"
              title="Ordered List"
              type="button"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M2 5a1 1 0 011-1h1a1 1 0 010 2H3a1 1 0 01-1-1zm0 5a1 1 0 011-1h1a1 1 0 110 2H3a1 1 0 01-1-1zm0 5a1 1 0 011-1h1a1 1 0 110 2H3a1 1 0 01-1-1zm4-10a1 1 0 01.7-.3h10a1 1 0 110 2h-10a1 1 0 01-.7-.3 1 1 0 010-1.4 1 1 0 010 0zm0 5a1 1 0 01.7-.3h10a1 1 0 110 2h-10a1 1 0 01-.7-.3 a1 1 0 010-1.4 1 1 0 010 0zm0 5a1 1 0 01.7-.3h10a1 1 0 110 2h-10a1 1 0 01-.7-.3 a1 1 0 010-1.4 1 1 0 010 0z" clipRule="evenodd"/>
              </svg>
            </button>
            <button
              onClick={() => handleButtonClick("insertUnorderedList")}
              className="px-3 py-1.5 bg-white hover:bg-gray-100 focus:outline-none focus:bg-gray-200"
              title="Unordered List"
              type="button"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M2 5a1 1 0 100-2 1 1 0 000 2zm0 5a1 1 0 100-2 1 1 0 000 2zm0 5a1 1 0 100-2 1 1 0 000 2zm4-10a1 1 0 01.7-.3h10a1 1 0 110 2h-10a1 1 0 01-.7-.3 a1 1 0 010-1.4 1 1 0 010 0zm0 5a1 1 0 01.7-.3h10a1 1 0 110 2h-10a1 1 0 01-.7-.3 a1 1 0 010-1.4 1 1 0 010 0zm0 5a1 1 0 01.7-.3h10a1 1 0 110 2h-10a1 1 0 01-.7-.3 a1 1 0 010-1.4 1 1 0 010 0z" clipRule="evenodd"/>
              </svg>
            </button>
          </div>

          {/* Insert Tools */}
          <div className="flex border border-gray-300 rounded-md overflow-hidden mr-2">
            <button
              onClick={() => setIsLinkModalOpen(true)}
              className="px-3 py-1.5 bg-white hover:bg-gray-100 focus:outline-none focus:bg-gray-200 border-r border-gray-300"
              title="Insert Link (Ctrl+K)"
              type="button"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/>
              </svg>
            </button>

            <button
              onClick={() => setIsImageModalOpen(true)}
              className="px-3 py-1.5 bg-white hover:bg-gray-100 focus:outline-none focus:bg-gray-200 border-r border-gray-300"
              title="Insert Image"
              type="button"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
              </svg>
            </button>

            <button
              onClick={() => setIsTableModalOpen(true)}
              className="px-3 py-1.5 bg-white hover:bg-gray-100 focus:outline-none focus:bg-gray-200 border-r border-gray-300"
              title="Insert Table"
              type="button"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 4a1 1 0 00-1 1v10a1 1 0 001 1h10a1 1 0 001-1V5a1 1 0 00-1-1H5zM4 5a2 2 0 012-2h10a2 2 0 012 2v10a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm2 2h2v2H6V7zm0 4h2v2H6v-2zm0 4h2v2H6v-2zm4-8h2v2h-2V7zm0 4h2v2h-2v-2zm0 4h2v2h-2v-2zm4-8h2v2h-2V7zm0 4h2v2h-2v-2zm0 4h2v2h-2v-2z" clipRule="evenodd"/>
              </svg>
            </button>

            <button
              onClick={() => setIsFormulaModalOpen(true)}
              className="px-3 py-1.5 bg-white hover:bg-gray-100 focus:outline-none focus:bg-gray-200"
              title="Insert Formula"
              type="button"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h1a1 1 0 100-2H7zm4 0a1 1 0 000 2h.01a1 1 0 100-2H11zm-3 4a1 1 0 011-1h4a1 1 0 110 2H9a1 1 0 01-1-1z" clipRule="evenodd"/>
              </svg>
            </button>
          </div>

          {/* Clear Formatting */}
          <button
            // onClick={clearFormatting}
            className="px-3 py-1.5 bg-white border border-gray-300 rounded-md hover:bg-gray-100 focus:outline-none focus:bg-gray-200"
            title="Clear Formatting"
            type="button"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-6 0H5m2 0h4M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
            </svg>
          </button>
        </div>

        {/* Editor Content Area */}
        <div
          ref={editorRef}
          contentEditable
          dir="ltr"
          style={{
            textAlign: "left",
            direction: "ltr",
            unicodeBidi: "plaintext",
            minHeight: "400px",
            padding: "1.5rem",
            fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
            fontSize: "16px",
            outline: "none",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            lineHeight: "1.6",
            caretColor: "auto",
          }}
          className="prose max-w-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-all"
          onBlur={() => {
            const newContent = editorRef.current.innerHTML;
            onChange?.(newContent);
          }}
        />

        {/* Modals */}
        <Modal isOpen={isLinkModalOpen} onClose={() => setIsLinkModalOpen(false)} title="Insert Link">
          <div>
            <label className="block text-sm font-medium mb-1">Link Text (Optional)</label>
            <input
              type="text"
              value={linkText}
              onChange={(e) => setLinkText(e.target.value)}
              placeholder="Display text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">URL *</label>
            <input
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              autoFocus
            />
          </div>
          <div className="flex justify-end space-x-2 mt-3">
            <button
              type="button"
              onClick={() => setIsLinkModalOpen(false)}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Cancel
            </button>
            <button
              type="button" onClick={handleLinkInsert}
              className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Insert Link
            </button>
          </div>
        </Modal>

        <Modal isOpen={isImageModalOpen} onClose={() => setIsImageModalOpen(false)} title="Insert Image">
          <div className="space-y-4">
            <button
              onClick={() => setIsImageLibraryModalOpen(true)}
              className="w-full px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              type="button"
            >
              Choose from Library
            </button>

            <div className="text-center text-gray-500">OR</div>

            <div>
              <label className="block mb-2 font-medium">Upload Image:</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={isUploading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {isUploading && (
                <div className="mt-2 text-center text-blue-600">
                  Uploading...
                </div>
              )}
            </div>

            <div className="text-center text-gray-500">OR</div>

            <div>
              <label className="block mb-2 font-medium">Image URL:</label>
              <input
                type="url"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://example.com/image.jpg"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex justify-end mt-4">
              <button
                type="button"
                onClick={handleImageInsert}
                className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Insert from URL
              </button>
            </div>
          </div>
        </Modal>

        <Modal
          isOpen={isImageLibraryModalOpen}
          onClose={() => setIsImageLibraryModalOpen(false)}
          title="Select Image from Library"
          size="lg"
        >
          {imageLibrary.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {imageLibrary.map((image, index) => (
                <div
                  key={index}
                  className="border rounded-md overflow-hidden cursor-pointer transition-transform hover:scale-105"
                  onClick={() => handleImageFromLibrary(image.file_name || image.url)}
                >
                  <img
                    src={image.file_name || image.url}
                    alt={image.alt || `Library image ${index}`}
                    className="w-full h-32 object-cover"
                  />
                  <div className="p-2">
                    <p className="text-sm truncate">{image.name || `Image ${index + 1}`}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-4">No images available in the library.</p>
          )}
        </Modal>

        <Modal
          isOpen={isTableModalOpen}
          onClose={() => setIsTableModalOpen(false)}
          title="Insert Table"
          size="sm"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Rows</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={tableRows}
                  onChange={(e) => setTableRows(parseInt(e.target.value) || 1)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Columns</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={tableCols}
                  onChange={(e) => setTableCols(parseInt(e.target.value) || 1)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={() => setIsTableModalOpen(false)}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleTableInsert}
                className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Insert Table
              </button>
            </div>
          </div>
        </Modal>

        <Modal
          isOpen={isFormulaModalOpen}
          onClose={() => setIsFormulaModalOpen(false)}
          title="Insert Formula"
        >
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Formula Type</label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="inline"
                  checked={formulaType === "inline"}
                  onChange={(e) => setFormulaType(e.target.value)}
                  className="mr-2"
                />
                Inline Equation
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="block"
                  checked={formulaType === "block"}
                  onChange={(e) => setFormulaType(e.target.value)}
                  className="mr-2"
                />
                Block Equation
              </label>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">LaTeX Formula</label>
            <input
              type="text"
              value={formulaText}
              onChange={(e) => setFormulaText(e.target.value)}
              placeholder="x^2 + y^2 = z^2, \frac{a}{b}, \sum_{n=1}^\infty \frac{1}{n^2}"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          </div>

          {/* Live Preview */}
          {formulaText && (
            <div className="mb-4 p-3 border rounded-md bg-gray-50">
              <label className="block text-sm font-medium mb-2">Preview:</label>
              <div className="min-h-[40px] flex items-center justify-center">
                <MathJax dynamic>
                  {formulaType === "block" 
                    ? `\\[${formulaText}\\]`
                    : `\\(${formulaText}\\)`
                  }
                </MathJax>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={() => setIsFormulaModalOpen(false)}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleFormulaInsert}
              disabled={!formulaText}
              className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Insert Formula
            </button>
          </div>
        </Modal>
      </div>
    </MathJaxContext>
  );
};

export default MyTextEditor;